import React, { useState, useEffect } from 'react';
import { useNavigate,useLocation } from 'react-router';
import { useOnboarding } from '../../../context/OnboardingContext';
import { Login, Register } from '../../auth';
import { PublicWizardLayout, WizardContainer } from '../WizardLayout';
import Stepper from '../Stepper';
import { createOnboardingSession } from '../../../services/wizardApi';
import '../../../styles/wizard.css';
import axiosInstance from '../../../api/axiosInstance';
import { toast } from 'react-toast';
import {
  LuCircleCheck,
  LuLogIn,
  LuUserPlus,
  LuSave,
  LuBuilding2,
  LuFolderKanban,
  LuFlaskConical,
  LuPlay,
} from 'react-icons/lu';
import { INTENT_TYPES } from '../../../Config/Onboarding/constant';

const INTENT_TITLES = {
  [INTENT_TYPES.MANUAL_TO_AUTOMATION]: 'Convert manual tests to automation',
  [INTENT_TYPES.MIGRATE_SCRIPTS]: 'Migrate existing automation scripts',
  [INTENT_TYPES.JIRA_REQUIREMENTS]: 'Generate tests from requirements',
  [INTENT_TYPES.SAMPLE_PROJECT]: 'Explore with a sample project',
};

const NEXT_STEPS = [
  { label: 'Create your workspace', icon: LuBuilding2 },
  { label: 'Set up your first project', icon: LuFolderKanban },
  { label: 'Add or generate test cases', icon: LuFlaskConical },
  { label: 'Run your first execution', icon: LuPlay },
];

const AuthGate = () => {
  const navigate = useNavigate();
  const { state, setSessionId, loadSession, resetOnboarding } = useOnboarding();
  const [activeTab, setActiveTab] = useState('login');
  const [isLoading, setIsLoading] = useState(false);
  const location = useLocation();
  // Switch tab based on query params (e.g. after registration or email verification)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('return_to') === 'wizard' || params.get('tab') === 'signin') {
      setActiveTab('signup');
      navigate(location.pathname, { replace: true });
    }
  }, [location.search, location.pathname, navigate]);

  // On mount / state change: if intent is null -> redirect to get-started
  // If intent exists, check local token and validate it. If valid, go to step 2.
  useEffect(() => {
    const verifyIntentAndToken = async () => {
      const intent = state?.engine?.intent;

      // If intent is explicitly null/undefined/empty -> send to get-started
      if (intent === null || intent === undefined || intent === '') {
          navigate('/get-started');
        return;
      }

      // If there's an intent value, try to validate existing token
      if (intent) {
        const userStr = localStorage.getItem('user');
        if (!userStr) return;

        let user;
        try {
          user = JSON.parse(userStr);
        } catch (err) {
          console.warn('Failed to parse user from localStorage', err);
          return;
        }

        const token = user?.accessToken || user?.access_token || user?.token;
        if (!token) return;

        try {
          const res = await axiosInstance.get('/users/verify_token');
          if (res?.is_token_valid) {
          const lastInProgressStep = state.engine?.flow?.stepOrder?.filter(
          stepId => state.engine?.steps?.[stepId]?.status === 'in_progress'
        ).pop() || 2;
          navigate(`/app/onboarding?step=${lastInProgressStep}`);
          }
          else{
            toast.error('Session expired. Please log in again to continue your onboarding.');
          }
        } catch (err) {
          console.error('Error verifying token:', err);
        }
      }
    };

    verifyIntentAndToken();
  }, [state, navigate]);

  // Listen for successful authentication
  // useEffect(() => {
  //   const checkAuth = setInterval(() => {
  //     const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
      
  //     if (isAuthenticated) {
  //       handleAuthSuccess();
  //       clearInterval(checkAuth);
  //     }
  //   }, 500);

  //   return () => clearInterval(checkAuth);
  // }, []);

  const handleAuthSuccess = async () => {
    setIsLoading(true);
    
    try {
      // Create/restore onboarding session on backend
      const response = await createOnboardingSession({
        ...state,
        currentStep: state.currentStep
      });

      if (response.success) {
        setSessionId(response.data.session_id);
        
        // If there's existing session data, load it
        if (response.data.payload) {
          await loadSession(response.data.payload);
        }
      }

      // Redirect to private onboarding flow
      // setTimeout(() => {
      //   navigate('/app/onboarding');
      // }, 500);
      
    } catch (error) {
      console.error('Error creating onboarding session:', error);
      // Still proceed to onboarding even if session creation fails
      navigate('/app/onboarding');
    } finally {
      setIsLoading(false);
    }
  };

  // 'login' tab renders the sign-up form; 'signup' renders sign-in (existing tab semantics).
  const isSignIn = activeTab === 'signup';
  const intentTitle = INTENT_TITLES[state?.engine?.intent];

  return (
    <PublicWizardLayout>
      <WizardContainer>
        <Stepper
          currentStep={1}
          onStepClick={(step) => {
            if (step === 1) navigate('/get-started');
          }}
        />
        <div className="ob-authgate">
          <aside className="ob-authgate__aside">
            <span className="ob-authgate__eyebrow">
              <LuCircleCheck size={14} /> Goal selected
            </span>
            <h2 className="ob-authgate__aside-title">
              {intentTitle || 'Your guided setup'}
            </h2>
            <p className="ob-authgate__aside-desc">
              {isSignIn
                ? 'Sign in and we’ll pick up your setup right where you left it.'
                : 'Create your account to save this setup and continue building your workspace.'}
            </p>

            <ol className="ob-authgate__next">
              {NEXT_STEPS.map(({ label, icon: Icon }, i) => (
                <li key={label} className={i === 0 ? 'is-current' : ''}>
                  <span className="ob-authgate__next-icon">
                    <Icon size={14} />
                  </span>
                  <span>{label}</span>
                </li>
              ))}
            </ol>

            <div className="ob-authgate__saved">
              <LuSave size={15} />
              <span>Your progress is saved. After signing in, you’ll continue from where you left off.</span>
            </div>
          </aside>

          <section className="ob-authgate__card">
            <header className="ob-authgate__header">
              <span className="ob-authgate__icon">
                {isSignIn ? <LuLogIn size={18} /> : <LuUserPlus size={18} />}
              </span>
              <div>
                <h1 className="ob-authgate__title">
                  {isSignIn ? 'Welcome back' : 'Create your account'}
                </h1>
                <p className="ob-authgate__subtitle">
                  {isSignIn
                    ? 'Sign in to continue your onboarding journey'
                    : 'Sign up to start automating your tests'}
                </p>
              </div>
            </header>

            <div className="ob-authgate__tabs" role="tablist" aria-label="Account">
              <button
                type="button"
                role="tab"
                aria-selected={!isSignIn}
                className={`ob-authgate__tab ${!isSignIn ? 'is-active' : ''}`}
                onClick={() => setActiveTab('login')}
              >
                <LuUserPlus size={15} /> Sign up
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={isSignIn}
                className={`ob-authgate__tab ${isSignIn ? 'is-active' : ''}`}
                onClick={() => setActiveTab('signup')}
              >
                <LuLogIn size={15} /> Sign in
              </button>
            </div>

            <div className="ob-authgate__body" key={activeTab}>
              {isLoading ? (
                <div className="wizard-loading">
                  <div className="wizard-loading-spinner"></div>
                  <p className="wizard-loading-text">Setting up your workspace...</p>
                </div>
              ) : isSignIn ? (
                <Login embedded={true} />
              ) : (
                <Register embedded={true} />
              )}
            </div>

          </section>
        </div>
      </WizardContainer>
    </PublicWizardLayout>
  );
};

export default AuthGate;